import axios from 'axios';
import {
  AuthToken,
  LoginCredentials,
  Property,
  PropertyFilterOptions,
  PropertySearch,
  PropertySearchResponse,
  PropertyShowcaseResponse,
} from '../types';
import type { MapPin } from '../components/property/propertyClusters';
import { getAdministrativeLoginPath, isControlPlaneHost } from '../utils/runtime';
import { getApiBaseUrl } from '../utils/apiBase';

export const API_BASE_URL = getApiBaseUrl();

export const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: false
});

// Instância com timeout longo para operações de IA (edição de imagem, geração, etc)
export const apiLongTimeout = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutos para operações de IA
  withCredentials: false
});

// Função para configurar interceptors de request (reutilizável)
const setupRequestInterceptor = (instance: typeof api) => {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!isControlPlaneHost()) {
      // @AI_COMMENTS: Adicionar headers para identificação do tenant
      const currentHost = window.location.hostname;
      const currentPort = window.location.port || '3100';

      // Nota: o browser nao permite definir Host diretamente.
      config.headers['X-Original-Host'] = `${currentHost}:${currentPort}`;
      config.headers['X-Tenant-Domain'] = currentHost;
    } else {
      delete config.headers['X-Original-Host'];
      delete config.headers['X-Tenant-Domain'];
    }

    return config;
  });
};

// Aplicar interceptors em ambas as instâncias
setupRequestInterceptor(api);
setupRequestInterceptor(apiLongTimeout);

// Controle para evitar múltiplos redirecionamentos
let isRedirecting = false;

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Se a requisição foi cancelada, não fazer nada
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath.includes('/login');
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      // Só tratamos sessão expirada DENTRO da área administrativa.
      // Em páginas públicas (home, imóveis, etc.) um token inválido não deve
      // arrastar o visitante para o /admin/login.
      const isAdminArea =
        currentPath.startsWith('/admin') || currentPath.startsWith('/superadmin');

      // Token inválido: limpa silenciosamente em qualquer área
      localStorage.removeItem('access_token');
      delete api.defaults.headers.common['Authorization'];

      // Redireciona para o login apenas quando o usuário está no admin
      if (isAdminArea && !isLoginPage && !isLoginRequest && !isRedirecting) {
        isRedirecting = true;
        setTimeout(() => {
          const next = `${window.location.pathname}${window.location.search}`;
          const separator = getAdministrativeLoginPath().includes('?') ? '&' : '?';
          window.location.replace(
            `${getAdministrativeLoginPath()}${separator}next=${encodeURIComponent(next)}`
          );
        }, 100);
      }
    }
    
    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthToken> {
    // Criar form-data urlencoded
    const params = new URLSearchParams();
    params.append('username', credentials.username);
    params.append('password', credentials.password);
    
    const response = await api.post('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    
    const token = response.data;
    localStorage.setItem('access_token', token.access_token);
    return token;
  },

  logout() {
    localStorage.removeItem('access_token');
  },

  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return !!token;
  },

  getToken(): string | null {
    return localStorage.getItem('access_token');
  },
};

export const propertyService = {
  async getAll(params?: any) {
    const response = await api.get('/properties/', { params });
    return response.data;
  },

  async getFeatured() {
    const response = await api.get('/properties/featured');
    return response.data;
  },

  async getShowcase(limit: number = 6): Promise<PropertyShowcaseResponse> {
    const response = await api.get<PropertyShowcaseResponse>('/properties/showcase', {
      params: { limit },
    });
    return response.data;
  },

  async getMapPins(params?: {
    type?: string;
    transaction_type?: string;
    city?: string;
    neighborhood?: string;
  }): Promise<MapPin[]> {
    const response = await api.get<MapPin[]>('/properties/map', { params });
    return response.data;
  },

  async getFilterOptions(): Promise<PropertyFilterOptions> {
    const response = await api.get<PropertyFilterOptions>('/properties/filter-options');
    return response.data;
  },

  async search(searchParams: PropertySearch): Promise<PropertySearchResponse | Property[]> {
    const response = await api.get<PropertySearchResponse | Property[]>(
      '/properties/search',
      { params: searchParams }
    );
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get(`/properties/${id}`);
    return response.data;
  },

  async getSimilar(id: string, limit: number = 6): Promise<Property[]> {
    const response = await api.get<Property[]>(
      `/properties/${encodeURIComponent(id)}/similar`,
      { params: { limit } },
    );
    return response.data;
  },

  async create(property: any) {
    const response = await api.post('/properties/', property);
    return response.data;
  },

  async update(id: string, property: any) {
    const response = await api.put(`/properties/${id}`, property);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete(`/properties/${id}`);
    return response.data;
  },

  async getStats() {
    const response = await api.get('/properties/stats/overview');
    return response.data;
  },

  async getByBroker(brokerId: number, relationType: string = 'both') {
    const response = await api.get(`/properties/by-broker/${brokerId}`, {
      params: { relation_type: relationType }
    });
    return response.data;
  },

  async sendMessage(propertyId: string, message: any) {
    const response = await api.post(`/properties/${propertyId}/messages`, message);
    return response.data;
  },

  async approve(id: string) {
    const response = await api.post(`/properties/${id}/approve`);
    return response.data;
  },

  async reject(id: string) {
    const response = await api.post(`/properties/${id}/reject`);
    return response.data;
  },

  async getForFollowUp(params?: any) {
    // @AI_COMMENTS: Filtrar parâmetros vazios
    const cleanParams = params ? Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as any) : {};
    
    const response = await api.get('/properties/follow-up', { params: cleanParams });
    return response.data;
  },

  async sendVerificationMessage(propertyId: string) {
    const response = await api.post(`/properties/${propertyId}/verify`);
    return response.data;
  },

  async updatePropertyStatus(propertyId: string, status: string, verified: boolean) {
    const response = await api.patch(`/properties/${propertyId}/status`, {
      status,
      verified
    });
    return response.data;
  },

  // Grava só a sequência da galeria; a capa continua sendo escolha separada.
  async updateImagesOrder(propertyId: string, images: string[]) {
    const response = await api.patch(`/properties/${propertyId}/images/order`, { images });
    return response.data;
  },
};

export const canalProService = {
  async getConfig() {
    try {
      const response = await api.get('/canal-pro/config');
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null; 
      }
      throw error;
    }
  },

  async createOrUpdateConfig(config: any) {
    const response = await api.post('/canal-pro/config', config);
    return response.data;
  },

  async testConnection(config: any) {
    const response = await api.post('/canal-pro/test-connection', config);
    return response.data;
  },

  async importProperties() {
    const response = await api.post('/canal-pro/import');
    return response.data;
  },

  async getImportHistory(limit: number = 100) {
    const response = await api.get(`/canal-pro/import-history?limit=${limit}`);
    return response.data;
  },

  async getSyncOverview() {
    const response = await api.get('/canal-pro/sync/overview');
    return response.data;
  },

  async getPropertyPublication(propertyId: string) {
    const response = await api.get('/canal-pro/sync/publications', {
      params: { property_id: propertyId, limit: 1 }
    });
    return response.data.items?.[0] || null;
  },

  async setPropertyChannelStatus(propertyId: string, desiredStatus: string) {
    const response = await api.patch(`/canal-pro/sync/properties/${propertyId}/status`, {
      desired_status: desiredStatus
    });
    return response.data;
  },
};

export const statsService = {
  getSystemStats: async () => {
    const response = await api.get('/stats/system');
    return response.data;
  }
};

export const contentService = {
  async getByKey(key: string) {
    const response = await api.get(`/content/key/${encodeURIComponent(key)}`);
    return response.data;
  },

  async updateByKey(key: string, content: any) {
    const response = await api.put(`/content/${key}`, content);
    return response.data;
  },

  async getSiteSettings() {
    const response = await api.get('/content/site/settings');
    return response.data;
  },

  async updateSiteSettings(settings: any) {
    const response = await api.put('/content/settings/site', settings);
    return response.data;
  },

  async getAvailableTemplates() {
    const response = await api.get('/content/templates/available');
    return response.data;
  },
};

export const blogService = {
  async getPosts(params: { skip?: number; limit?: number } = {}) {
    const response = await api.get('/content/blog/posts', { params });
    return response.data;
  },

  async getPost(slug: string) {
    const response = await api.get(`/content/blog/posts/${encodeURIComponent(slug)}`);
    return response.data;
  },
};

export const leadService = {
  async create(lead: any) {
    const response = await api.post('/leads', lead);
    return response.data;
  },

  async getAll(params?: any) {
    const response = await api.get('/leads', { params });
    return response.data;
  },

  async getRecent() {
    const response = await api.get('/leads/recent');
    return response.data;
  },

  async getById(id: number) {
    const response = await api.get(`/leads/${id}`);
    return response.data;
  },

  async getByType(clientType: string, params?: any) {
    const response = await api.get(`/leads/by-type/${clientType}`, { params });
    return response.data;
  },

  async getByStatus(status: string, params?: any) {
    const response = await api.get(`/leads/status/${status}`, { params });
    return response.data;
  },

  async getClientTypes() {
    const response = await api.get('/leads/client-types');
    return response.data;
  },

  async update(id: number, lead: any) {
    const response = await api.put(`/leads/${id}`, lead);
    return response.data;
  },

  async delete(id: number) {
    const response = await api.delete(`/leads/${id}`);
    return response.data;
  },

  async getStats() {
    const response = await api.get('/leads/stats/overview');
    return response.data;
  },

  // Pipeline methods
  async getPipelines() {
    const response = await api.get('/leads/pipelines/');
    return response.data;
  },

  async getDefaultPipeline() {
    const response = await api.get('/leads/pipelines/default');
    return response.data;
  },

  async createPipeline(pipeline: any) {
    const response = await api.post('/leads/pipelines/', pipeline);
    return response.data;
  },

  async updatePipeline(id: number, pipeline: any) {
    const response = await api.put(`/leads/pipelines/${id}`, pipeline);
    return response.data;
  },

  async deletePipeline(id: number) {
    const response = await api.delete(`/leads/pipelines/${id}`);
    return response.data;
  },

  async setDefaultPipeline(id: number) {
    const response = await api.post(`/leads/pipelines/${id}/set-default`);
    return response.data;
  },

  // Kanban methods
  async getKanbanBoard(pipelineId?: number) {
    const params = pipelineId ? { pipeline_id: pipelineId } : {};
    const response = await api.get('/leads/kanban/', { params });
    return response.data;
  },

  async moveClientInKanban(moveRequest: any) {
    const response = await api.post('/leads/kanban/move', moveRequest);
    return response.data;
  },

  // Pipeline stages methods
  async getPipelineStages(pipelineId: number) {
    const response = await api.get(`/leads/pipelines/${pipelineId}/stages`);
    return response.data;
  },

  async createPipelineStage(pipelineId: number, stage: any) {
    const response = await api.post(`/leads/pipelines/${pipelineId}/stages`, stage);
    return response.data;
  },

  async updatePipelineStage(id: number, stage: any) {
    const response = await api.put(`/leads/pipeline-stages/${id}`, stage);
    return response.data;
  },

  async deletePipelineStage(id: number) {
    const response = await api.delete(`/leads/pipeline-stages/${id}`);
    return response.data;
  },

  async reorderPipelineStages(pipelineId: number, stagePositions: any[]) {
    const response = await api.post(`/leads/pipelines/${pipelineId}/stages/reorder`, {
      stage_positions: stagePositions
    });
    return response.data;
  },
};

export const brokerService = {
  async getPublicActive() {
    const response = await api.get('/brokers/public/active');
    return response.data;
  },

  async getAll(params?: any) {
    const response = await api.get('/brokers/', { params });
    return response.data;
  },

  async getActive() {
    const response = await api.get('/brokers/active');
    return response.data;
  },

  async create(broker: any) {
    const response = await api.post('/brokers/', broker);
    return response.data;
  },

  async update(id: number, broker: any) {
    const response = await api.put(`/brokers/${id}`, broker);
    return response.data;
  },

  async delete(id: number) {
    const response = await api.delete(`/brokers/${id}`);
    return response.data;
  },

  async activate(id: number) {
    const response = await api.put(`/brokers/${id}/activate`);
    return response.data;
  },

  async uploadPhoto(id: number, photo: File) {
    const formData = new FormData();
    formData.append('file', photo);
    const response = await api.post(`/brokers/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async removePhoto(id: number) {
    const response = await api.delete(`/brokers/${id}/photo`);
    return response.data;
  },
};

export const categoryService = {
  async getPublic() {
    const response = await api.get('/categories/public');
    return response.data;
  },

  async getFeatured() {
    const response = await api.get('/categories/featured');
    return response.data;
  },

  async getAll(params?: any) {
    const response = await api.get('/categories/', { params });
    return response.data;
  },

  async getById(id: number) {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },

  async create(category: any) {
    const response = await api.post('/categories/', category);
    return response.data;
  },

  async update(id: number, category: any) {
    const response = await api.put(`/categories/${id}`, category);
    return response.data;
  },

  async delete(id: number) {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },

  async getProperties(id: number, params?: any) {
    const response = await api.get(`/categories/${id}/properties`, { params });
    return response.data;
  },

  async updateFeatured(categoryIds: number[]) {
    const response = await api.put('/categories/featured', categoryIds);
    return response.data;
  },
};

export const mediaService = {
  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  async uploadBrokerImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/media/upload/broker', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Função utilitária para construir URLs de imagem
export const buildImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  
  // Se já é uma URL completa (base64 ou http), retorna como está
  if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // Se já inclui /api/v1/, construir URL completa com encoding
  if (imagePath.startsWith('/api/v1/')) {
    // Separar a parte da URL da parte do arquivo
    const urlParts = imagePath.split('/');
    // Codificar apenas a parte do nome do arquivo (último elemento)
    const fileName = urlParts[urlParts.length - 1];
    const encodedFileName = encodeURIComponent(fileName);
    urlParts[urlParts.length - 1] = encodedFileName;
    
    return `${API_BASE_URL}${urlParts.join('/')}`;
  }
  
  // Se começa com /, remove para evitar dupla barra
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  
  return `${API_BASE_URL}/${cleanPath}`;
};

// Função utilitária para construir URLs de vídeo
export const buildVideoUrl = (videoPath: string): string => {
  if (!videoPath) return '';
  
  // Se já é uma URL completa, retorna como está
  if (videoPath.startsWith('http')) {
    return videoPath;
  }
  
  // Para vídeos locais, usar buildImageUrl que já trata paths corretamente
  return buildImageUrl(videoPath);
};

export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: (limit = 10) => api.get(`/dashboard/recent-activity?limit=${limit}`)
};

// ===================== Lotes de Mídia (Media Batches) =====================

export interface MediaBatchImage {
  id: number;
  url: string;
  thumbnail_url?: string | null;
  original_filename?: string | null;
  width?: number | null;
  height?: number | null;
  watermarked?: boolean;
  ai_edited?: boolean;
}

export interface AIAction {
  key: string;
  label: string;
  needs_style: boolean;
}

export interface AIOptions {
  actions: AIAction[];
  styles: string[];
  engines: string[];
  engines_ready?: Record<string, boolean>;
}

export interface MediaBatchSummary {
  id: number;
  name: string;
  description?: string | null;
  images_count: number;
  cover_url?: string | null;
  created_at?: string | null;
}

export interface MediaBatchDetail extends MediaBatchSummary {
  images: MediaBatchImage[];
}

export const mediaBatchService = {
  async list(): Promise<MediaBatchSummary[]> {
    const response = await api.get('/media-batches');
    return response.data;
  },

  async get(id: number): Promise<MediaBatchDetail> {
    const response = await api.get(`/media-batches/${id}`);
    return response.data;
  },

  async create(name: string, description?: string): Promise<MediaBatchDetail> {
    const response = await api.post('/media-batches', { name, description });
    return response.data;
  },

  async update(id: number, data: { name?: string; description?: string }): Promise<MediaBatchDetail> {
    const response = await api.put(`/media-batches/${id}`, data);
    return response.data;
  },

  async remove(id: number): Promise<void> {
    await api.delete(`/media-batches/${id}`);
  },

  async uploadFiles(
    id: number,
    files: File[],
    onProgress?: (percent: number) => void
  ): Promise<MediaBatchDetail> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await api.post(`/media-batches/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // uploads em lote podem demorar
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    });
    return response.data;
  },

  async deleteImage(batchId: number, imageId: number): Promise<void> {
    await api.delete(`/media-batches/${batchId}/images/${imageId}`);
  },

  async downloadZip(id: number, fileName: string, imageIds?: number[]): Promise<void> {
    const params = imageIds && imageIds.length ? { ids: imageIds.join(',') } : undefined;
    const response = await api.get(`/media-batches/${id}/download`, {
      responseType: 'blob',
      timeout: 300000,
      params,
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async attachToProperty(
    batchId: number,
    propertyId: string,
    options?: { imageIds?: number[]; setCover?: boolean }
  ): Promise<{ success: boolean; attached_count: number; total_images: number; message: string }> {
    const response = await api.post(`/media-batches/${batchId}/attach-to-property`, {
      property_id: propertyId,
      image_ids: options?.imageIds ?? null,
      set_cover: options?.setCover ?? false,
    });
    return response.data;
  },

  async reapplyWatermark(
    batchId: number
  ): Promise<{ success: boolean; applied: number; already_watermarked: number; total: number; message: string }> {
    const response = await api.post(`/media-batches/${batchId}/reapply-watermark`);
    return response.data;
  },

  async aiOptions(): Promise<AIOptions> {
    const response = await api.get('/media-batches/ai/options');
    return response.data;
  },

  async aiEdit(
    batchId: number,
    imageId: number,
    payload: { action: string; engine: string; style?: string; extra?: string }
  ): Promise<{ token: string; preview_url: string }> {
    const response = await api.post(
      `/media-batches/${batchId}/images/${imageId}/ai-edit`,
      payload,
      { timeout: 240000 } // edição de imagem pode levar mais de 1 min
    );
    return response.data;
  },

  async aiSave(
    batchId: number,
    imageId: number,
    payload: { token: string; mode: 'new' | 'replace' }
  ): Promise<MediaBatchDetail> {
    const response = await api.post(
      `/media-batches/${batchId}/images/${imageId}/ai-save`,
      payload
    );
    return response.data;
  },

  async createProperty(
    batchId: number,
    payload: {
      imageIds?: number[];
      useAi?: boolean;
      description?: string;
      title?: string;
      type?: string;
      transactionType?: string;
      price?: number;
    }
  ): Promise<{
    success: boolean;
    property_id: string;
    title: string;
    images_count: number;
    ai_used: boolean;
    ai_model?: string;
    ai_extraction_method?: string;
    ai_warnings?: string[];
    missing_fields?: string[];
    extracted_fields?: string[];
    message: string;
  }> {
    const response = await api.post(
      `/media-batches/${batchId}/create-property`,
      {
        image_ids: payload.imageIds && payload.imageIds.length ? payload.imageIds : null,
        use_ai: payload.useAi ?? false,
        description: payload.description ?? null,
        title: payload.title ?? null,
        type: payload.type ?? null,
        transaction_type: payload.transactionType ?? null,
        price: payload.price ?? null,
      },
      { timeout: 120000 } // extração com IA pode levar alguns segundos
    );
    return response.data;
  },
};
